import { Injectable } from "@angular/core";
import {
    CircuitDirection,
    CircuitElement,
    EndpointDeviceElement,
    EndpointType,
    PointsReposition,
    SourceOrTarget
} from "../models/diagram.models";
import { TOPOLOGY_SETTINGS } from "../models/diagram.constants";

/**
 * Classe utilitária para diagrama do circuito DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmCircuitDiagramUtilsService {

    private readonly FIRST_HORIZONTAL_SEGMENT_MAP = { source: [0, 1], target: [2, 3] };
    private readonly VERTICAL_SEGMENT:   Array<number> = [1, 2];
    private readonly DEFAULT_REPOSITION: PointsReposition = { offset: 0, points: [] };
    private readonly FIRST_TRANSPONDER:  number = 0;
    private readonly SECOND_TRANSPONDER: number = 1;
    private readonly THIRD_TRANSPONDER:  number = 2;
    private readonly LAST_TRANSPONDER:   number = 3;


    /**
     * Responsável por calcular o posicionamento do eixo X dos links internos de transponder, para evitar que eles se sobreponham.
     *
     * @param end enpoint de um circuito
     * @param transponder um transponder específico.
     * @returns objeto contendo o offset de deslocamento e a lista de índices do eixo X que deverão sofrer alterações no
     *          posicionamento.
     */
    getTransponderLinkXAxisOffset(end: EndpointDeviceElement<EndpointType>,
            transponder: CircuitElement, direction: CircuitDirection): PointsReposition {
        const linkXOffsetByTransponder = {
            [CircuitDirection.TOWARDS_Z_END]: {
                [EndpointType.A_END]: {
                    [this.LAST_TRANSPONDER]:  { offset: -30, points: this.VERTICAL_SEGMENT }
                },
                [EndpointType.Z_END]: {
                    [this.LAST_TRANSPONDER]:  { offset:  30, points: this.VERTICAL_SEGMENT }
                }
            },
            [CircuitDirection.TOWARDS_A_END]: {
                [EndpointType.A_END]: {
                    [this.FIRST_TRANSPONDER]: { offset: -30, points: this.VERTICAL_SEGMENT }
                },
                [EndpointType.Z_END]: {
                    [this.FIRST_TRANSPONDER]: { offset:  30, points: this.VERTICAL_SEGMENT }
                }
            }
        };
        const transponderIndex = end.transponders.findIndex(end => end.id === transponder.id);
        const reposition = linkXOffsetByTransponder[direction][end.endpointType][transponderIndex];

        return reposition || this.DEFAULT_REPOSITION;
    }

    /**
     * Responsável por calcular o posicionamento do eixo X dos links internos de transponder, para evitar que eles se sobreponham.
     *
     * @param end enpoint de um circuito
     * @param transponder um transponder específico.
     * @returns objeto contendo o offset de deslocamento e a lista de índices do eixo X que deverão sofrer alterações no
     *          posicionamento.
     */
    getTransponderToAmpLinkXAxisOffset(end: EndpointDeviceElement<EndpointType>,
            transponder: CircuitElement): PointsReposition {
        const linkXOffsetByTransponder = {
            [EndpointType.A_END]: {
                [this.FIRST_TRANSPONDER]: { offset: -10, points: this.VERTICAL_SEGMENT },
                [this.LAST_TRANSPONDER]:  { offset:  10, points: this.VERTICAL_SEGMENT }
            },
            [EndpointType.Z_END]: {
                [this.FIRST_TRANSPONDER]: { offset:  10, points: this.VERTICAL_SEGMENT },
                [this.LAST_TRANSPONDER]:  { offset: -10, points: this.VERTICAL_SEGMENT }
            }
        };
        const transponderIndex = end.transponders.findIndex(end => end.id === transponder.id);
        const reposition = linkXOffsetByTransponder[end.endpointType][transponderIndex];

        return reposition || this.DEFAULT_REPOSITION;
    }

    /**
     * Responsável por calcular o posicionamento do eixo X dos links internos de transponder, para evitar que eles se sobreponham.
     *
     * @param end enpoint de um circuito
     * @param transponder um transponder específico.
     * @returns objeto contendo o offset de deslocamento e a lista de índices do eixo X que deverão sofrer alterações no
     *          posicionamento.
     */
    getTransponderSiblingToAmpLinkXAxisOffset(end: EndpointDeviceElement<EndpointType>,
            transponder: CircuitElement, direction: CircuitDirection): PointsReposition {
        const linkXOffsetByTransponder = {
            [CircuitDirection.TOWARDS_Z_END]: {
                [EndpointType.A_END]: {
                    [this.LAST_TRANSPONDER]:  { offset: -45, points: this.VERTICAL_SEGMENT }
                },
                [EndpointType.Z_END]: {
                    [this.LAST_TRANSPONDER]:  { offset:  45, points: this.VERTICAL_SEGMENT }
                }
            },
            [CircuitDirection.TOWARDS_A_END]: {
                [EndpointType.A_END]: {
                    [this.FIRST_TRANSPONDER]: { offset: -45, points: this.VERTICAL_SEGMENT }
                },
                [EndpointType.Z_END]: {
                    [this.FIRST_TRANSPONDER]: { offset:  45, points: this.VERTICAL_SEGMENT }
                }
            }
        };
        const transponderIndex = end.transponders.findIndex(end => end.id === transponder.id);
        const reposition = linkXOffsetByTransponder[direction][end.endpointType][transponderIndex];

        return reposition || this.DEFAULT_REPOSITION;
    }

    getTransponderLinkYAxisOffset(end: EndpointDeviceElement<EndpointType>,
            transponder: CircuitElement, direction: CircuitDirection, basedOn: SourceOrTarget = "source"): PointsReposition {
        const [ topOffset, bottomOffet ] = this.getTransponderYAxisOffset();
        const points = this.FIRST_HORIZONTAL_SEGMENT_MAP[basedOn];

        const linkYOffsetByTransponder = {
            [CircuitDirection.TOWARDS_Z_END]: {
                [this.FIRST_TRANSPONDER]:  { offset: topOffset,   points, basedOn },
                [this.SECOND_TRANSPONDER]: { offset:         4,   points, basedOn },
                [this.THIRD_TRANSPONDER]:  { offset:         4,   points, basedOn },
                [this.LAST_TRANSPONDER]:   { offset: topOffset,   points, basedOn }
            },
            [CircuitDirection.TOWARDS_A_END]: {
                [this.FIRST_TRANSPONDER]:  { offset: bottomOffet, points, basedOn },
                [this.SECOND_TRANSPONDER]: { offset:          30, points, basedOn },
                [this.THIRD_TRANSPONDER]:  { offset:          30, points, basedOn },
                [this.LAST_TRANSPONDER]:   { offset: bottomOffet, points, basedOn }
            }
        };
        const transponderIndex = end.transponders.findIndex(end => end.id === transponder.id);
        const reposition = linkYOffsetByTransponder[direction][transponderIndex];

        return reposition || this.DEFAULT_REPOSITION;
    }

    /**
     * Responsável por calcular o posicionamento do eixo X dos links internos de transponder, para evitar que eles se sobreponham.
     *
     * @param end enpoint de um circuito
     * @param transponder um transponder específico.
     * @returns objeto contendo o offset de deslocamento e a lista de índices do eixo X que deverão sofrer alterações no
     *          posicionamento.
     */
    getTransponderToAmpLinkYAxisOffset(end: EndpointDeviceElement<EndpointType>,
            transponder: CircuitElement, direction: CircuitDirection, basedOn: SourceOrTarget = "source"): PointsReposition {
        const [ topOffset, bottomOffet ] = this.getTransponderYAxisOffset();
        const points = this.FIRST_HORIZONTAL_SEGMENT_MAP[basedOn];

        const linkYOffsetByTransponder = {
            [CircuitDirection.TOWARDS_Z_END]: {
                [this.FIRST_TRANSPONDER]:  { offset: topOffset, points },
                [this.SECOND_TRANSPONDER]: { offset:         5, points },
                [this.THIRD_TRANSPONDER]:  { offset:         5, points },
                [this.LAST_TRANSPONDER]:   { offset: topOffset, points }
            },
            [CircuitDirection.TOWARDS_A_END]: {
                [this.FIRST_TRANSPONDER]:  { offset: bottomOffet, points },
                [this.SECOND_TRANSPONDER]: { offset:          31, points },
                [this.THIRD_TRANSPONDER]:  { offset:          31, points },
                [this.LAST_TRANSPONDER]:   { offset: bottomOffet, points }
            }
        };

        const transponderIndex = end.transponders.findIndex(end => end.id === transponder.id);
        const reposition = linkYOffsetByTransponder[direction][transponderIndex];

        return reposition || this.DEFAULT_REPOSITION;
    }

    private getTransponderYAxisOffset(): number[] {
        const transponderHeight = TOPOLOGY_SETTINGS.TRANSPONDER.INITIAL.height;
        const topOffset = transponderHeight / 3;
        const bottomOffet = topOffset * 2;

        return [ topOffset, bottomOffet ];
    }
}
